/**
 * Panneau « logs techniques » repliable (§3.3).
 *
 * Le défilement automatique s'arrête dès que l'utilisateur remonte dans le
 * flux : rien n'est plus agaçant qu'une console qui vous ramène en bas pendant
 * que vous lisez une erreur. Un bouton « Revenir en bas » réactive le suivi.
 */

import clsx from 'clsx';
import { ArrowDown, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useClipboard } from '../hooks/useClipboard';
import { formatTime } from '../lib/format';
import { MAX_LOG_LINES, type LogEntry } from '../lib/logs';
import { Button, CopyFeedback, FOCUS_RING } from './ui';

/** Tolérance, en pixels, pour considérer que l'on est « en bas ». */
const BOTTOM_THRESHOLD_PX = 24;

/** Couleur de chaque niveau de log. */
const LEVEL_CLASSES: Record<string, string> = {
  debug: 'text-slate-500 dark:text-slate-500',
  info: 'text-slate-700 dark:text-slate-300',
  warn: 'text-amber-700 dark:text-amber-400',
  error: 'text-red-700 dark:text-red-400',
};

/** Propriétés du panneau. */
export interface LogPanelProps {
  logs: readonly LogEntry[];
  /** Ouvert par défaut sur l'écran d'échec, replié pendant un déploiement. */
  defaultOpen?: boolean;
  title?: string;
  /** Contenu additionnel dans l'en-tête (bouton de chargement complet…). */
  actions?: ReactNode;
}

/** Flux de logs techniques. */
export function LogPanel({
  logs,
  defaultOpen = true,
  title = 'Logs techniques',
  actions,
}: LogPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [following, setFollowing] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { status, copy } = useClipboard();

  // Défilement automatique, uniquement si l'utilisateur n'a pas remonté.
  useEffect(() => {
    if (!open || !following) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [logs, open, following]);

  const onScroll = (): void => {
    const node = scrollRef.current;
    if (!node) return;
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    setFollowing(distanceFromBottom <= BOTTOM_THRESHOLD_PX);
  };

  const backToBottom = (): void => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
    setFollowing(true);
  };

  const asText = (): string =>
    logs.map((entry) => `[${entry.at}] [${entry.step}] [${entry.level}] ${entry.line}`).join('\n');

  return (
    <section className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className={clsx(
            'inline-flex items-center gap-2 rounded px-1 py-1 text-sm font-medium',
            'text-slate-800 dark:text-slate-200',
            FOCUS_RING,
          )}
        >
          {open ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
          {title}
          <span className="font-normal text-slate-500 dark:text-slate-400">
            ({logs.length}
            {logs.length >= MAX_LOG_LINES ? ' — les plus anciennes ont été élaguées' : ''})
          </span>
        </button>

        <div className="ml-auto flex items-center gap-2 text-xs">
          <CopyFeedback status={status} />
          {actions}
          <Button variant="ghost" onClick={() => copy(asText())} disabled={logs.length === 0}>
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            Copier
          </Button>
        </div>
      </div>

      {open ? (
        <div className="relative border-t border-slate-200 dark:border-slate-800">
          <div
            ref={scrollRef}
            onScroll={onScroll}
            tabIndex={0}
            role="log"
            aria-label={title}
            aria-live="polite"
            className={clsx(
              'h-80 overflow-auto bg-slate-950 px-3 py-2 font-mono text-xs leading-5 text-slate-300',
              FOCUS_RING,
            )}
          >
            {logs.length === 0 ? (
              <p className="text-slate-500">
                Aucune ligne pour l'instant. Les logs du pipeline apparaîtront ici dès que le
                builder démarrera.
              </p>
            ) : (
              logs.map((entry, index) => (
                <div
                  key={entry.seq ?? `snapshot-${index}`}
                  className="whitespace-pre-wrap break-all"
                >
                  <span className="text-slate-600">{formatTime(entry.at)} </span>
                  <span className="text-slate-500">[{entry.step}] </span>
                  <span className={LEVEL_CLASSES[entry.level] ?? 'text-slate-300'}>
                    {entry.line}
                  </span>
                </div>
              ))
            )}
          </div>

          {!following ? (
            <button
              type="button"
              onClick={backToBottom}
              className={clsx(
                'absolute bottom-3 right-4 inline-flex items-center gap-1.5 rounded-full',
                'bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-lg hover:bg-indigo-500',
                FOCUS_RING,
              )}
            >
              <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
              Revenir en bas
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
